import { useCallback, useEffect, useMemo, useState } from 'react';
import base64url from 'base64url';
import { Label, SelectLabelOption } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useSelectLabelSearch } from '../../hooks/useSelectLabelSearch';

interface UseOefsProps {
  label: TimesheetLabel;
}

interface UseOefsResultProps {
  options: SelectLabelOption[];
  handleSearch: (query: string) => void;
  loading: boolean;
  hasMore: boolean;
}

export const isThereMore = (
  cursor: number | undefined,
  totalCount: number | undefined
): boolean => {
  return cursor && totalCount ? cursor < totalCount : false;
};

export const useOefs = ({ label }: UseOefsProps): UseOefsResultProps => {
  const [options, setOptions] = useState<SelectLabelOption[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const { searchHandler, data } = useSelectLabelSearch();
  const loading = useMemo(() => Boolean(!data), [data]);

  const handleSearch = useCallback(
    (query: string) => {
      searchHandler({
        id: label.id,
        query: query || undefined
      });
    },
    [label.id, searchHandler]
  );

  useEffect(() => {
    if (loading) {
      setOptions([]);
      setHasMore(false);
    } else if (data) {
      const items = (data.label as Label)?.selectOptions?.items || [];
      const cursor = data?.label?.selectOptions?.cursor
        ? parseInt(base64url.decode(data?.label?.selectOptions?.cursor))
        : undefined;
      const totalCount = data?.label?.selectOptions?.totalCount;
      setOptions(items);
      setHasMore(isThereMore(cursor, totalCount));
    }
  }, [data, loading]);

  return useMemo(
    () => ({
      options,
      handleSearch,
      loading,
      hasMore
    }),
    [options, handleSearch, loading, hasMore]
  );
};

export default useOefs;
